import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, AlertCircle, KeyRound, Lock, Shield } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needs2FA, setNeeds2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, needs2FA ? totpCode : null);
      if (result?.requires_2fa) {
        setNeeds2FA(true);
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B1426] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#0066FF] rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading font-bold text-xl text-white tracking-tight">WorldPay</span>
          </div>
          <p className="text-[11px] text-slate-500 tracking-[0.15em] uppercase ml-[52px]">Back-Office Administratif</p>
        </div>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl font-bold text-white leading-tight tracking-tight mb-4">
            Plateforme de gestion<br />des operations bancaires
          </h2>
          <p className="text-slate-400 text-base leading-relaxed max-w-md">
            Gerez vos virements internationaux, suivez les transactions et administrez les comptes depuis une interface securisee.
          </p>
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-6 text-slate-500 text-xs">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
              Systeme operationnel
            </span>
            <span>Connexion chiffree SSL</span>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#0B1426] rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="text-left">
                <h1 className="font-heading font-bold text-lg text-slate-900 tracking-tight">WorldPay</h1>
                <p className="text-[11px] text-slate-400 tracking-[0.15em] uppercase">Back-Office</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <div className="mb-7">
              <h2 className="font-heading font-bold text-xl text-[#0F172A] tracking-tight">
                {needs2FA ? 'Verification 2FA' : 'Connexion'}
              </h2>
              <p className="text-slate-500 text-sm mt-1.5">
                {needs2FA ? 'Saisissez le code de votre application' : 'Accedez a votre espace de gestion'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200 text-sm rounded-lg">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {!needs2FA ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Adresse e-mail</Label>
                    <Input
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11 rounded-lg border-slate-200 focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Mot de passe</Label>
                    <Input
                      type="password"
                      placeholder="Votre mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 rounded-lg border-slate-200 focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                      data-testid="login-password-input"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 justify-center text-slate-400">
                    <KeyRound className="w-4 h-4" />
                    <span className="text-xs">Code a 6 chiffres</span>
                  </div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    required
                    className="h-14 rounded-lg text-center text-2xl tracking-[0.5em] font-mono border-slate-200 focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                    data-testid="login-totp-input"
                    autoFocus
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-11 bg-[#0066FF] hover:bg-[#0052CC] text-white rounded-lg font-medium transition-colors duration-200"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{needs2FA ? 'Verification...' : 'Connexion...'}</>
                ) : needs2FA ? 'Verifier' : (
                  <><Lock className="mr-2 h-4 w-4" />Se connecter</>
                )}
              </Button>

              {needs2FA && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-500 rounded-lg text-sm hover:bg-slate-50"
                  onClick={() => { setNeeds2FA(false); setTotpCode(''); setError(''); }}
                  data-testid="login-back-btn"
                >
                  Retour
                </Button>
              )}
            </form>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Plateforme securisee - Acces reserve
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
